import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { FaHeart, FaRegComment, FaEye, FaSearch } from "react-icons/fa";
import AOS from "aos";
import "aos/dist/aos.css";
import "./blog.css";

const categories = [
  "Festivals",
  "Relationship",
  "Transits",
  "Vedic Astrology",
  "Health and Prosperity",
  "Yoga",
];

const sortOptions = ["New to Old", "Old to New", "Most Liked", "Most Viewed"];

const BlogPage = () => {
  const [blogData, setBlogData] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [sortBy, setSortBy] = useState("New to Old");
  const [visibleCount, setVisibleCount] = useState(6);
  const [searchTerm, setSearchTerm] = useState("");
  const [scrollProgress, setScrollProgress] = useState(0);

  const baseImagePath = "https://dashboard.jyotirvani.com/uploads/blog/";

  useEffect(() => {
    AOS.init({
      duration: 900,
      once: true,
      offset: 80,
    });
  }, []);

  useEffect(() => {
    fetch("https://dashboard.jyotirvani.com/api/fetchblog")
      .then((res) => res.json())
      .then((data) => {
        if (data.status && Array.isArray(data.data)) {
          setBlogData(data.data);
        } else {
          console.error("Blog fetch error:", data.message);
        }
      })
      .catch((err) => console.error("API fetch failed:", err));
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const totalHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      const progress = totalHeight > 0 ? (window.scrollY / totalHeight) * 100 : 0;
      setScrollProgress(progress);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const stripHtml = (html) => {
    return html ? html.replace(/<[^>]+>/g, "") : "";
  };

  const filteredBlogs = useMemo(() => {
    return [...blogData]
      .filter((blog) => {
        const matchesCategory =
          !selectedCategory || blog.category === selectedCategory;

        const cleanTitle = stripHtml(blog.title).toLowerCase();
        const cleanDesc = stripHtml(blog.description).toLowerCase();
        const cleanCategory = (blog.category || "").toLowerCase();
        const search = searchTerm.toLowerCase();

        const matchesSearch =
          !search ||
          cleanTitle.includes(search) ||
          cleanDesc.includes(search) ||
          cleanCategory.includes(search);

        return matchesCategory && matchesSearch;
      })
      .sort((a, b) => {
        switch (sortBy) {
          case "Old to New":
            return new Date(a.created_at) - new Date(b.created_at);
          case "Most Liked":
            return (b.likes || 0) - (a.likes || 0);
          case "Most Viewed":
            return (b.views || 0) - (a.views || 0);
          default:
            return new Date(b.created_at) - new Date(a.created_at);
        }
      });
  }, [blogData, selectedCategory, sortBy, searchTerm]);

  const featuredBlog = filteredBlogs[0];
  const otherBlogs = filteredBlogs.slice(1, visibleCount + 1);

  const recentBlogs = [...blogData]
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 3);

  const handleShowMore = () => {
    setVisibleCount((prev) => prev + 6);
  };

  return (
    <>
      <div
        className="reading-progress-bar"
        style={{ width: `${scrollProgress}%` }}
      ></div>

      <div className="blog-container">
        <div className="blog-top-header" data-aos="fade-down">
          <h1 className="blog-main-heading">Explore Our Astrology Blogs</h1>
          <p className="blog-subheading">
            Discover divine insights, festival guides, relationships, yoga,
            transits and more.
          </p>

          <div className="blog-search-bar">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search blogs by title, category or keyword..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setVisibleCount(6);
              }}
            />
          </div>
        </div>

        <h2 className="section-title" data-aos="fade-up">
          Recent Blogs
        </h2>

        <div className="recent-blogs-section">
          <div className="row">
            {recentBlogs.map((blog, index) => (
              <div
                key={blog.id}
                className="col-md-4 col-sm-12"
                data-aos="zoom-in"
                data-aos-delay={index * 100}
              >
                <div className="recent-blog-card">
                  <img
                    src={`${baseImagePath}${blog.image}`}
                    alt={blog.title}
                    className="blog-image"
                  />
                  <div className="blog-content">
                    <p className="blog-date">
                      {new Date(blog.created_at).toLocaleDateString()}
                    </p>
                    <h3 className="ra-blog-title">{blog.title}</h3>
                    <p className="blog-excerpt">
                      {stripHtml(blog.description).slice(0, 100)}...
                    </p>
                    <div className="ra-main-button-part">
                      <Link to={`/blog/${blog.id}`} className="read-more">
                        Read More
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="main-blog-section">
          <div className="blog-sidebar" data-aos="fade-right">
            <h2 className="sidebar-title">Blog Sort by</h2>
            {sortOptions.map((option) => (
              <label key={option} className="sidebar-label">
                <input
                  type="radio"
                  name="sort"
                  value={option}
                  checked={sortBy === option}
                  onChange={() => setSortBy(option)}
                  className="radio-input"
                />
                {option}
              </label>
            ))}

            <h2 className="sidebar-title">Blog Category</h2>
            {categories.map((cat) => (
              <label key={cat} className="sidebar-label">
                <input
                  type="radio"
                  name="category"
                  value={cat}
                  checked={selectedCategory === cat}
                  onChange={() => {
                    setSelectedCategory(cat);
                    setVisibleCount(6);
                  }}
                  className="radio-input"
                />
                {cat}
              </label>
            ))}

            <div className="clear-filter">
              <button
                onClick={() => {
                  setSelectedCategory("");
                  setSearchTerm("");
                  setVisibleCount(6);
                }}
                className="clear-button"
              >
                Clear Filter
              </button>
            </div>
          </div>

          <div className="blog-grid row">
            {featuredBlog && (
              <div className="col-12" data-aos="fade-up">
                <div className="featured-blog-card">
                  <div className="featured-image-wrap">
                    <img
                      src={`${baseImagePath}${featuredBlog.image}`}
                      alt={featuredBlog.title}
                      className="featured-blog-image"
                    />
                  </div>

                  <div className="featured-content">
                    <span className="featured-badge">Featured Blog</span>
                    <p className="blog-date">
                      {new Date(featuredBlog.created_at).toLocaleDateString()}
                    </p>
                    <h2 className="featured-title">{featuredBlog.title}</h2>
                    <p className="featured-excerpt">
                      {stripHtml(featuredBlog.description).slice(0, 220)}...
                    </p>

                    <div className="featured-footer">
                      <Link
                        to={`/blog/${featuredBlog.id}`}
                        className="read-more featured-read-more"
                      >
                        Read Full Article
                      </Link>

                      <div className="blog-meta">
                        <span className="ra-meta-item">
                          <FaHeart /> {featuredBlog.likes || 0}
                        </span>
                        <span className="ra-meta-item">
                          <FaEye /> {featuredBlog.views || 0}
                        </span>
                        <span className="ra-meta-item">
                          <FaRegComment /> {featuredBlog.comments || 0}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {otherBlogs.map((blog, index) => (
              <div
                key={blog.id}
                className="col-md-4 col-sm-12"
                data-aos="fade-up"
                data-aos-delay={index * 80}
              >
                <div className="blog-card">
                  <img
                    src={`${baseImagePath}${blog.image}`}
                    alt={blog.title}
                    className="blog-image"
                  />
                  <div className="blog-content">
                    <p className="blog-date">
                      {new Date(blog.created_at).toLocaleDateString()}
                    </p>
                    <h3 className="ra-blog-title">{blog.title}</h3>
                    <p className="blog-excerpt">
                      {stripHtml(blog.description).slice(0, 70)}...
                    </p>
                    <div className="ra-main-button-part">
                      <Link to={`/blog/${blog.id}`} className="read-more">
                        Read More
                      </Link>
                      <div className="blog-meta">
                        <span className="ra-meta-item">
                          <FaHeart /> {blog.likes || 0}
                        </span>
                        <span className="ra-meta-item">
                          <FaEye /> {blog.views || 0}
                        </span>
                        <span className="ra-meta-item">
                          <FaRegComment /> {blog.comments || 0}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {visibleCount < filteredBlogs.length - 1 && (
              <div className="show-more-container" data-aos="fade-up">
                <button onClick={handleShowMore} className="show-more-button">
                  Show More
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default BlogPage;